import { getKnowledgeBrowserEntryLabel } from "@app/components/data_source_view/browser/knowledgeBrowserItems";
import { useBrowsableSpaces } from "@app/components/data_source_view/browser/useBrowsableSpaces";
import { useKnowledgeBrowserItems } from "@app/components/data_source_view/browser/useKnowledgeBrowserItems";
import {
  getKnowledgeBrowserBreadcrumbItems,
  navigateToKnowledgeBrowserItem,
  useKnowledgeBrowserNavigation,
} from "@app/components/data_source_view/browser/useKnowledgeBrowserNavigation";
import type { AttachContextSlashCommand } from "@app/components/editor/extensions/shared/slash_suggestion/attachContextSlashCommand";
import {
  isAttachContextSlashCommand,
  SELECT_ATTACH_CONTEXT_SLASH_COMMAND_ACTION,
} from "@app/components/editor/extensions/shared/slash_suggestion/attachContextSlashCommand";
import { AttachContextSlashMenuItemIcon } from "@app/components/editor/extensions/shared/slash_suggestion/AttachContextSlashMenuItemIcon";
import type { SlashCommandSection } from "@app/components/editor/extensions/shared/slash_suggestion/buildSlashCommandSections";
import type {
  ContextSlashSearchSelection,
  ContextSlashSearchUseCase,
} from "@app/components/editor/extensions/shared/slash_suggestion/contextSlashSearchTypes";
import {
  buildBrowseCommands,
  buildRootBrowseSections,
  getAttachableNodeForItem,
  isLoadMoreKnowledgeBrowserSlashCommand,
  isNavigateKnowledgeBrowserSlashCommand,
} from "@app/components/editor/extensions/shared/slash_suggestion/knowledgeBrowserSlashCommands";
import type {
  SlashCommand,
  SlashCommandDropdownRef,
} from "@app/components/editor/extensions/shared/slash_suggestion/SlashCommandDropdown";
import { SlashCommandDropdown } from "@app/components/editor/extensions/shared/slash_suggestion/SlashCommandDropdown";
import type { SlashMenuStackFrame } from "@app/components/editor/extensions/shared/slash_suggestion/slashMenuNavigation";
import { useAttachContextSearchSections } from "@app/components/editor/extensions/shared/slash_suggestion/useAttachContextSearchSections";
import type { AttachContextSlashMenuItem } from "@app/components/editor/extensions/shared/slash_suggestion/useAttachContextSlashMenuItems";
import { useAttachContextSlashMenuItems } from "@app/components/editor/extensions/shared/slash_suggestion/useAttachContextSlashMenuItems";
import type { DataSourceViewContentNode } from "@app/types/data_source_view";
import { assertNeverAndIgnore } from "@app/types/shared/utils/assert_never";
import type { LightWorkspaceType } from "@app/types/user";
import { Breadcrumbs } from "@dust-tt/sparkle";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { SuggestionProps } from "@tiptap/suggestion";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";

const BROWSE_EMPTY_MESSAGE = msg`Nothing to browse here`;

function toSlashCommandItem(
  item: AttachContextSlashMenuItem
): AttachContextSlashCommand {
  return {
    action: SELECT_ATTACH_CONTEXT_SLASH_COMMAND_ACTION,
    data: { selection: item.selection },
    description: item.description,
    icon: () => <AttachContextSlashMenuItemIcon item={item} />,
    id: item.id,
    label: item.label,
  };
}

// What the list shows: the browsed level, the global search, or the scoped search plus the rest.
type SubMenuMode = "browse" | "search" | "scoped-search";

function getSubMenuMode({
  query,
  canNavigateUp,
}: {
  query: string;
  canNavigateUp: boolean;
}): SubMenuMode {
  if (query.trim().length === 0) {
    return "browse";
  }
  return canNavigateUp ? "scoped-search" : "search";
}

type SubMenuListProps =
  | { items: SlashCommand[] }
  | { sections: SlashCommandSection[] };

function getSubMenuListProps({
  mode,
  isRoot,
  rootSection,
  browseSections,
  browseCommands,
  searchCommands,
  searchSections,
}: {
  mode: SubMenuMode;
  isRoot: boolean;
  rootSection: SlashCommandSection | null;
  browseSections: SlashCommandSection[];
  browseCommands: SlashCommand[];
  searchCommands: SlashCommand[];
  searchSections: SlashCommandSection[];
}): SubMenuListProps {
  switch (mode) {
    case "search":
      return { items: searchCommands };
    case "scoped-search":
      return { sections: searchSections };
    case "browse":
      // The root renders labelled sections; they carry their own loading state.
      if (!isRoot) {
        return { items: browseCommands };
      }
      return {
        sections: rootSection
          ? [...browseSections, rootSection]
          : browseSections,
      };
    default:
      assertNeverAndIgnore(mode);
      return { items: searchCommands };
  }
}

interface AttachContextSubMenuDropdownProps extends Pick<
  SuggestionProps<SlashCommand>,
  "clientRect" | "query"
> {
  // The sub-menu command the user came from, and how to return to it. Absent when the browser is
  // the whole menu: no Back row, Escape closes.
  activeFrame?: SlashMenuStackFrame;
  onBack?: () => void;
  conversationId?: string | null;
  dropdownHeaders?: React.ReactNode;
  onClose: () => void;
  // Extra rows shown as the last section of the browser's root with an empty query, for a menu
  // where the browser is the whole menu and commands such as "Upload file" have nowhere else to go.
  // Last so the first space stays the default highlight.
  rootSection?: { label: string; items: SlashCommand[] };
  onRootSectionSelect?: (item: SlashCommand) => void;
  onSelect: (selection: ContextSlashSearchSelection) => void;
  owner: LightWorkspaceType;
  spaceId?: string | null;
  useCase: ContextSlashSearchUseCase;
}

interface AttachContextSubMenuDropdownRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
  // Always provided; optional so a shared sub-menu ref keeps satisfying the type.
  navigateUp?: () => boolean;
}

/**
 * @cc [owner:smb2268,label:product] browse-when-query-empty
 * With an empty query the sub-menu MUST list the current navigation level of the knowledge
 * browser (spaces at the root, then categories, data source views and folder contents). A
 * non-empty query at the root MUST show the global search results; below the root it MUST show
 * two sections, the matches within the browsed level first and then the remaining global results
 * with those matches removed, while keeping the breadcrumbs and the navigation state. Back and
 * Escape MUST leave the sub-menu from any level; Backspace on an empty query MUST go up one level
 * while below the root (skipping levels the pod shortcut owns) and leave the sub-menu from the
 * root; a leading space in the query MUST NOT dismiss it. Without `activeFrame` and `onBack` the
 * browser is the whole menu: it MUST offer no Back row, and Escape, like Backspace on an empty
 * query at the root, MUST close the menu.
 */
export const AttachContextSubMenuDropdown = forwardRef<
  AttachContextSubMenuDropdownRef,
  AttachContextSubMenuDropdownProps
>(
  (
    {
      activeFrame,
      clientRect,
      conversationId = null,
      dropdownHeaders,
      onBack,
      onClose,
      onRootSectionSelect,
      onSelect,
      owner,
      query,
      rootSection,
      spaceId = null,
      useCase,
    },
    ref
  ) => {
    const { t } = useLingui();
    const dropdownRef = useRef<SlashCommandDropdownRef>(null);

    const excludeNonRemoteDatabaseTables = useCase === "skill-builder";

    const {
      emptyMessage,
      hasMinimalQuery,
      isLoading,
      items,
      spaces: scopedSpaces,
    } = useAttachContextSlashMenuItems({
      conversationId,
      owner,
      query,
      spaceId,
      useCase,
    });

    // `scopedSpaces` is what the search may look into; like the Agent Builder, the browser only
    // offers the subset that holds something to browse.
    const { spaces, isLoading: isBrowsableSpacesLoading } = useBrowsableSpaces({
      owner,
      spaces: scopedSpaces,
    });

    const navigation = useKnowledgeBrowserNavigation({ spaces });
    const { navigationHistory, navigateTo, navigateUp } = navigation;
    const currentEntry = navigationHistory[navigationHistory.length - 1];
    const canNavigateUp = navigationHistory.length > 1;
    const mode = getSubMenuMode({ query, canNavigateUp });

    const browser = useKnowledgeBrowserItems({
      owner,
      spaces,
      navigationHistory,
      viewType: "all",
      excludeNonRemoteDatabaseTables,
    });

    const isBrowseLoading =
      isLoading || isBrowsableSpacesLoading || browser.isLoading;
    const browseSections = useMemo(
      () =>
        buildRootBrowseSections(browser.items, {
          isLoading: isBrowseLoading,
          t,
        }),
      [browser.items, isBrowseLoading, t]
    );
    const attachNode = useCallback(
      (node: DataSourceViewContentNode) =>
        onSelect({ kind: "knowledge", node }),
      [onSelect]
    );
    const browseCommands = useMemo(
      () =>
        buildBrowseCommands(browser.items, {
          onAttachNode: attachNode,
          hasMore: browser.hasMore,
          isLoadingMore: browser.isLoadingMore,
          t,
        }),
      [attachNode, browser.items, browser.hasMore, browser.isLoadingMore, t]
    );
    const searchCommands = useMemo(
      () => items.map(toSlashCommandItem),
      [items]
    );
    const categoryDataSourceViews = useMemo(
      () =>
        browser.items.flatMap((item) =>
          item.kind === "data_source" ? [item.dataSourceView] : []
        ),
      [browser.items]
    );
    const searchSections = useAttachContextSearchSections({
      categoryDataSourceViews,
      emptyMessage,
      enabled: mode === "scoped-search",
      excludeNonRemoteDatabaseTables,
      hasMinimalQuery,
      isGlobalLoading: isLoading,
      items,
      navigationHistory,
      onAttachNode: attachNode,
      owner,
      query,
      toCommand: toSlashCommandItem,
    });

    const breadcrumbs = useMemo(
      () =>
        canNavigateUp ? (
          // Keep the editor focused: a focused breadcrumb button would trap focus in the menu.
          // `w-0 min-w-full` gives the trail a definite width inside the Radix scroll viewport,
          // whose `display: table` wrapper would otherwise grow with the trail and clip it.
          <div
            className="w-0 min-w-full px-2 py-1"
            onMouseDown={(event) => event.preventDefault()}
          >
            <Breadcrumbs
              collapseIntermediates
              items={getKnowledgeBrowserBreadcrumbItems(
                navigationHistory,
                navigateTo,
                {
                  getLabel: (entry) => getKnowledgeBrowserEntryLabel(entry, t),
                }
              )}
              size="xs"
            />
          </div>
        ) : undefined,
      [canNavigateUp, navigateTo, navigationHistory, t]
    );

    const handleSelect = (item: SlashCommand) => {
      if (isNavigateKnowledgeBrowserSlashCommand(item)) {
        navigateToKnowledgeBrowserItem(item.data.item, navigation);
      } else if (isLoadMoreKnowledgeBrowserSlashCommand(item)) {
        if (!browser.isLoadingMore) {
          void browser.loadMore();
        }
      } else if (isAttachContextSlashCommand(item)) {
        onSelect(item.data.selection);
      } else if (rootSection?.items.some((row) => row.id === item.id)) {
        onRootSectionSelect?.(item);
      }
    };

    useImperativeHandle(
      ref,
      () => ({
        onKeyDown: ({ event }) => {
          // Shift+Enter attaches a highlighted folder or data source instead of entering it.
          if (event.key === "Enter" && event.shiftKey) {
            const highlighted = dropdownRef.current?.getHighlightedItem?.();
            const attachable =
              highlighted && isNavigateKnowledgeBrowserSlashCommand(highlighted)
                ? getAttachableNodeForItem(highlighted.data.item)
                : null;
            if (attachable) {
              event.preventDefault();
              attachNode(attachable);
              return true;
            }
          }

          if (event.key === "Backspace" && query.trim().length === 0) {
            event.preventDefault();
            if (canNavigateUp) {
              navigateUp();
            } else {
              onClose();
            }
            return true;
          }

          return dropdownRef.current?.onKeyDown({ event }) ?? false;
        },
        navigateUp: () => {
          if (!canNavigateUp) {
            return false;
          }
          navigateUp();
          return true;
        },
      }),
      [attachNode, canNavigateUp, navigateUp, onClose, query]
    );

    return (
      <SlashCommandDropdown
        ref={dropdownRef}
        clientRect={clientRect}
        command={handleSelect}
        dropdownHeaders={dropdownHeaders}
        emptyMessage={
          mode === "browse" ? t(BROWSE_EMPTY_MESSAGE) : emptyMessage
        }
        headerContent={breadcrumbs}
        isLoading={
          mode === "browse" ? isBrowseLoading : mode === "search" && isLoading
        }
        onClose={onClose}
        {...getSubMenuListProps({
          mode,
          isRoot: currentEntry.type === "root",
          rootSection: rootSection ?? null,
          browseSections,
          browseCommands,
          searchCommands,
          searchSections,
        })}
        subMenuNavigation={
          activeFrame && onBack
            ? { label: activeFrame.command.label, onBack }
            : undefined
        }
        size="wide"
      />
    );
  }
);

AttachContextSubMenuDropdown.displayName = "AttachContextSubMenuDropdown";
